/**
 * Reglas de la fecha de entrega de un ticket, en el lado del cliente.
 *
 * Ventana elegible: desde 10 días HÁBILES por delante (el plazo mínimo que
 * necesita el equipo), sin tope por arriba. Sábados y domingos bloqueados, y
 * el pasado nunca es elegible.
 *
 * Es el gemelo de `server/utils/fechaLimite.js`. Aquí sirve para pintar el
 * calendario y avisar antes de enviar; la validación que manda es la del
 * servidor, que es la que no se puede saltar.
 */

export const DIAS_HABILES_MINIMOS = 10;

/** Fecha en AAAA-MM-DD, que es como viaja al servidor. */
export const aIso = (fecha) => {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${mes}-${dia}`;
};

/** Interpreta AAAA-MM-DD como fecha local: `new Date('2026-09-02')` es UTC y se desplaza un día. */
export const desdeIso = (texto) => {
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(texto || '').trim());
  if (!partes) return null;
  const fecha = new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]));
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

export const esFinDeSemana = (fecha) => fecha.getDay() === 0 || fecha.getDay() === 6;

/** Hoy a medianoche: comparar contra fechas con hora da falsos negativos. */
export const hoy = () => {
  const ahora = new Date();
  return new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
};

export const mismoDia = (a, b) => Boolean(a) && Boolean(b)
  && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Primer día hábil desde `fecha` (ella misma si ya lo es). */
export const primerDiaHabil = (fecha) => {
  const cursor = new Date(fecha);
  while (esFinDeSemana(cursor)) cursor.setDate(cursor.getDate() + 1);
  return cursor;
};

/** Suma días hábiles, saltando fines de semana. */
export const sumarDiasHabiles = (fecha, dias) => {
  const cursor = new Date(fecha);
  let restantes = dias;
  while (restantes > 0) {
    cursor.setDate(cursor.getDate() + 1);
    if (!esFinDeSemana(cursor)) restantes -= 1;
  }
  return cursor;
};

/**
 * Extremos de la ventana elegible. Mínimo: 10 días hábiles por delante.
 * `hasta` en null significa que no hay tope: se puede comprometer tan lejos
 * como haga falta.
 */
export const rangoElegible = () => ({
  desde: sumarDiasHabiles(primerDiaHabil(hoy()), DIAS_HABILES_MINIMOS),
  hasta: null,
});

/** ¿Se puede elegir este día? */
export const esElegible = (fecha, rango = rangoElegible()) => (
  Boolean(fecha) && !esFinDeSemana(fecha) && fecha >= rango.desde
  && (!rango.hasta || fecha <= rango.hasta)
);

/** dd/mm/aaaa, el formato con el que se muestra en toda la aplicación. */
export const formatearFechaLimite = (valor) => {
  if (!valor) return '';
  const fecha = typeof valor === 'string' ? desdeIso(valor) : new Date(valor);
  if (!fecha || Number.isNaN(fecha.getTime())) return '';
  const dia = String(fecha.getDate()).padStart(2, '0');
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${fecha.getFullYear()}`;
};
